import type { AmiStatus, UtilityRow } from '../types/utility';

/**
 * The data routes. A utility's ami_status says which route TrueLoad knows for
 * it; routesFor() turns that, plus what else the row carries, into the set of
 * routes the picker offers. Every screen that used to test
 * `ami_status === 'supported'` reads this instead, so the vocabulary lives in
 * one place.
 */

export const AMI_STATUS_VALUES: AmiStatus[] = ['email_request', 'self_download', 'customer_request', 'connect', 'none'];

export const AMI_STATUS_LABELS: Record<AmiStatus, string> = {
  email_request: 'Email request',
  self_download: 'Customer self-download',
  customer_request: 'Customer requests from the utility',
  connect: 'Automatic connection',
  none: 'No known route',
};

export const AMI_STATUS_DESCRIPTIONS: Record<AmiStatus, string> = {
  email_request:
    'After the customer authorizes, TrueLoad emails the utility a data request. Needs a contact address.',
  self_download:
    'The customer exports the file from the utility portal and uploads it on the authorization page.',
  customer_request:
    'The customer asks the utility for their own data. With a request form link, the customer submits the utility’s web form, pre-filled, and the utility sends the file to the contractor. Without one, TrueLoad writes an email to the contact address and the customer uploads the file the utility sends back. TrueLoad never writes to the utility.',
  connect:
    'An automatic connection (Phase 2). Until it ships this reads as self-download.',
  none: 'No route is known. The picker offers on-site measurement.',
};

/**
 * Legacy values map onto the new vocabulary. Production still carries
 * supported/unsupported on the Vermont rows until the Phase 1 client is
 * promoted (see migration 20260908163808).
 */
export function normalizeAmiStatus(raw: string | null | undefined): AmiStatus {
  const value = (raw ?? '').trim();
  switch (value) {
    case 'email_request':
    case 'self_download':
    case 'customer_request':
    case 'connect':
    case 'none':
      return value;
    case 'supported':
      return 'email_request';
    case 'unsupported':
      return 'none';
    default:
      return 'none';
  }
}

/** The subset of a utility row the route rules read. */
export type RouteSource = Partial<
  Pick<
    UtilityRow,
    | 'name'
    | 'ami_status'
    | 'ami_contact_email'
    | 'portal_url'
    | 'download_steps'
    | 'export_format'
    | 'history_months'
    | 'rollout_note'
    | 'instructions_verified'
    | 'instructions_source_url'
    | 'request_form_template'
  >
>;

export type LinkRoute = 'self_download' | 'email_request' | 'customer_request';

/** Every route a send can take: the link routes, plus the utility contact pilot (utilityPilot.ts), which sends no link. */
export type SentRoute = LinkRoute | 'utility_request';

/**
 * The routes on which the homeowner uploads the file on their own page, and
 * sending it is the authorization: they downloaded it, or the utility sent it
 * to them after they asked.
 */
export function isUploadRoute(route: string | null | undefined): route is 'self_download' | 'customer_request' {
  return route === 'self_download' || route === 'customer_request';
}

export interface DataRoutes {
  status: AmiStatus;
  /** True when no utility row is known: "Other (not listed)" or nothing chosen. */
  unlisted: boolean;
  /** The homeowner exports the file and uploads it on the authorization page. */
  selfDownload: boolean;
  /** TrueLoad emails the utility after the homeowner authorizes. */
  emailRequest: boolean;
  /** The homeowner emails the utility themselves, then uploads what comes back. */
  customerRequest: boolean;
  /** An automatic connection exists for this utility (Phase 2). */
  connect: boolean;
  /** The card carries steps or a portal written for this utility. */
  hasInstructions: boolean;
  /** Which link-based route an authorization request takes by default. */
  primary: LinkRoute | null;
}

function present(value: string | null | undefined): boolean {
  return (value ?? '').trim().length > 0;
}

/**
 * Which routes apply to a utility. Null means no utility row: the contractor
 * chose "Other (not listed)", and the homeowner gets the generic steps that
 * most smart-meter portals follow.
 */
export function routesFor(utility: RouteSource | null | undefined): DataRoutes {
  if (!utility) {
    return {
      status: 'none',
      unlisted: true,
      selfDownload: true,
      emailRequest: false,
      customerRequest: false,
      connect: false,
      hasInstructions: false,
      primary: 'self_download',
    };
  }

  const status = normalizeAmiStatus(utility.ami_status);
  const hasInstructions = present(utility.download_steps) || present(utility.portal_url);
  // Steps written for a utility mean its customers can export, whatever the
  // status says; a contact address means an email request can be sent,
  // whatever the status says. The status settles which one the link takes.
  const selfDownload = status === 'self_download' || status === 'connect' || hasInstructions;
  // A utility that wants requests from its own customer gets no request from
  // TrueLoad: the customer submits the utility's form, or writes to the
  // contact address, instead.
  const customerRequest =
    status === 'customer_request' && (present(utility.request_form_template) || present(utility.ami_contact_email));
  const emailRequest = present(utility.ami_contact_email) && status !== 'customer_request';
  const connect = status === 'connect';

  let primary: LinkRoute | null = null;
  if (customerRequest) primary = 'customer_request';
  else if (status === 'email_request' && emailRequest) primary = 'email_request';
  else if (selfDownload) primary = 'self_download';
  else if (emailRequest) primary = 'email_request';

  return { status, unlisted: false, selfDownload, emailRequest, customerRequest, connect, hasInstructions, primary };
}

/**
 * The steps most smart-meter portals follow, for a utility with no card of its
 * own. Written to be true of PG&E, ComEd, National Grid and the other Green
 * Button portals, and hedged where they differ.
 */
export const GENERIC_DOWNLOAD_STEPS: string[] = [
  "Sign in to your electric utility's online account. The website is printed on your bill.",
  'Open the usage section. It is usually called My Usage, Energy Usage, Usage Details or Green Button.',
  'Look for Download My Data, Export, or Download usage data.',
  'Choose interval data at 15-minute detail if offered (hourly if that is all the portal has), and the longest date range available, at least the last 12 months.',
  'Choose Green Button XML or CSV as the format and save the file. If the portal emails the file, wait for that email.',
  'Come back to this page and upload the file.',
];

/** Split a stored steps field (one step per line) into clean steps. */
export function splitSteps(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(/\r?\n/)
    .map(line => line.replace(/^\s*(?:\d+[.)]|[-*•])\s*/, '').trim())
    .filter(line => line.length > 0);
}

/** The steps to show a homeowner: the utility's own, or the generic ones. */
export function stepsFor(utility: RouteSource | null | undefined): { steps: string[]; generic: boolean } {
  const own = splitSteps(utility?.download_steps);
  return own.length > 0 ? { steps: own, generic: false } : { steps: GENERIC_DOWNLOAD_STEPS, generic: true };
}

/** "12 months of history" or null when unknown. */
export function describeHistory(months: number | null | undefined): string | null {
  if (months == null || !Number.isFinite(months) || months <= 0) return null;
  if (months >= 24 && months % 12 === 0) return `${months / 12} years of history`;
  return `${months} month${months === 1 ? '' : 's'} of history`;
}

/** A portal address made safe to link to: https only, or nothing. */
export function safePortalUrl(url: string | null | undefined): string | null {
  const value = (url ?? '').trim();
  if (!value) return null;
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}
